import React from 'react';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
}

/**
 * Button — Sistema 2.0.
 * El primario es AMARILLO con texto navy: el momento de marca de cada pantalla
 * (regla: uno por vista). Todos los variantes comparten el mismo radio (12px),
 * el mismo press (scale 0.98) y el mismo anillo de foco amarillo.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className = '',
            variant = 'primary',
            size = 'md',
            isLoading = false,
            leftIcon,
            rightIcon,
            children,
            disabled,
            ...props
        },
        ref
    ) => {
        /* `disabled:opacity-50` sobre un relleno saturado da un amarillo lavado
         * que se lee como error de render. Un botón deshabilitado debe ser
         * NEUTRO y plano: mismo tamaño, sin color, sin sombra. */
        const baseStyles = 'inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:active:scale-100 disabled:bg-acero-100 disabled:text-acero-400 disabled:border-transparent disabled:shadow-none';

        const variants = {
            primary: 'bg-brand-400 hover:bg-brand-300 active:bg-brand-500 text-navy-950 shadow-s1',
            secondary: 'bg-navy-850 hover:bg-navy-700 active:bg-navy-900 text-white shadow-s1',
            outline: 'border border-acero-300 hover:border-acero-400 text-navy-950 bg-white hover:bg-acero-50 active:bg-acero-100',
            danger: 'bg-peligro hover:bg-[#B42318] active:bg-[#912018] text-white shadow-s1',
            ghost: 'bg-transparent hover:bg-acero-100 active:bg-acero-200 text-navy-700 hover:text-navy-950'
        };

        const sizes = {
            sm: 'h-8 px-3 text-xs gap-1.5',
            md: 'h-10 px-4 text-sm gap-2',
            lg: 'h-12 px-6 text-base gap-2.5'
        };

        return (
            <button
                ref={ref}
                disabled={disabled || isLoading}
                className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
                {...props}
            >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {!isLoading && leftIcon}
                {children}
                {!isLoading && rightIcon}
            </button>
        );
    }
);

Button.displayName = 'Button';
